import { z } from 'zod4';

import { fixture } from './fixtures';
import { Condition, GroupType } from './groups';
import { User } from './users';

// TypeScript enums
export enum IncentiveVisibility {
	Public = 'publicly_visible',
	Private = 'privately_visible',
	Team = 'immediate_team',
}

export enum IncentiveState {
	Active = 'active',
	Inactive = 'inactive',
}

export enum ClaimLimitPeriod {
	Week = 'week',
	Month = 'month',
	Quarter = 'quarter',
	Year = 'year',
	Lifetime = 'lifetime',
}

export type RecentClaimTimeframe = 'day' | 'week' | 'month';

// Base schemas
export const Incentive = z.object({
	id: z.string(),
	name: z.string(),
	giverId: z.string(),
	periodType: z.enum(ClaimLimitPeriod).nullable(),
	amount: z.number().nullable(),
	message: z.string(),
	iconUrl: z.string().nullable(),
	state: z.enum(IncentiveState).nullable(),
	visibility: z.enum(IncentiveVisibility),
	requireProof: z.boolean(),
	conditionsOperator: z.enum(GroupType).nullable(),
	conditions: z.array(Condition).nullable(),
	claimLimit: z.number().nullable(),
	quantity: z.number().nullable(),
	pendingApprovals: z.number(),
	type: z.string(),
});

export type Incentive = z.infer<typeof Incentive>;

export const IncentiveDetail = Incentive.extend({
	approvalLevels: z.array(z.array(User)),
	description: z.string(),
});

export type IncentiveDetail = z.infer<typeof IncentiveDetail>;

export const RecentClaim = z.object({
	timeframe: z.enum(['day', 'week', 'month']),
	receivers: z.array(User),
	incentive: Incentive,
});

export type RecentClaim = z.infer<typeof RecentClaim>;

export const IncentiveMetadata = z.object({
	incentive: Incentive,
	user: User,
	claimable: z.boolean(),
});

export type IncentiveMetadata = z.infer<typeof IncentiveMetadata>;

// Fixture functions
export const incentiveFixture = (overrides: Partial<Incentive> = {}): Incentive => ({
	id: fixture.id(),
	name: fixture.name('Incentive'),
	giverId: fixture.id(),
	periodType: ClaimLimitPeriod.Month,
	amount: fixture.int(50, 500),
	message: fixture.description('incentive'),
	iconUrl: fixture.url('icon'),
	state: IncentiveState.Active,
	visibility: IncentiveVisibility.Public,
	requireProof: false,
	conditionsOperator: GroupType.And,
	conditions: [],
	claimLimit: null,
	quantity: null,
	pendingApprovals: 0,
	type: 'incentive',
	...overrides,
});

export const incentiveDetailFixture = (overrides: Partial<IncentiveDetail> = {}): IncentiveDetail => ({
	id: fixture.id(),
	name: fixture.name('Incentive'),
	description: fixture.description('incentive'),
	giverId: fixture.id(),
	periodType: ClaimLimitPeriod.Month,
	amount: fixture.int(50, 500),
	message: fixture.description('incentive'),
	iconUrl: fixture.url('icon'),
	state: IncentiveState.Active,
	visibility: IncentiveVisibility.Public,
	requireProof: false,
	conditionsOperator: GroupType.And,
	conditions: [],
	claimLimit: null,
	quantity: null,
	pendingApprovals: 0,
	approvalLevels: [],
	type: 'incentive',
	...overrides,
});

export const recentClaimFixture = (overrides: Partial<RecentClaim> = {}): RecentClaim => ({
	timeframe: 'day',
	receivers: [],
	incentive: incentiveFixture(),
	...overrides,
});

export const incentiveMetadataFixture = (overrides: Partial<IncentiveMetadata> = {}): IncentiveMetadata => ({
	incentive: incentiveFixture(),
	user: { id: fixture.id(), displayName: fixture.name('User'), email: fixture.email(), profilePic: fixture.url('profile'), username: fixture.username() },
	claimable: true,
	...overrides,
});

// Utility functions
export const isIncentiveActive = (incentive: { state: IncentiveState | string | null }): boolean => incentive.state === IncentiveState.Active;
